package app.nestbridge.feature.children.presentation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import app.nestbridge.domain.identity.usecase.GetCurrentUserUseCase
import app.nestbridge.feature.children.model.Child
import app.nestbridge.feature.children.model.ChildStatus
import app.nestbridge.feature.children.usecase.SaveChildUseCase
import app.nestbridge.feature.children.screen.ChildDetailScreen
import app.nestbridge.feature.children.screen.CreateChildProfileEvent
import app.nestbridge.feature.children.screen.CreateChildProfileResult
import app.nestbridge.feature.children.screen.CreateChildProfileScreen
import app.nestbridge.feature.children.screen.CreateChildProfileState
import com.slack.circuit.codegen.annotations.CircuitInject
import com.slack.circuit.runtime.Navigator
import com.slack.circuit.runtime.presenter.Presenter
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import me.tatarka.inject.annotations.Assisted
import me.tatarka.inject.annotations.Inject
import software.amazon.lastmile.kotlin.inject.anvil.AppScope
import kotlin.time.Clock.System

@CircuitInject(
    screen = CreateChildProfileScreen::class,
    scope = AppScope::class,
)
@Inject
class CreateChildProfilePresenter(
    @Assisted private val screen: CreateChildProfileScreen,
    @Assisted private val navigator: Navigator,
    private val saveChildUseCase: SaveChildUseCase,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
) : Presenter<CreateChildProfileState> {

    @Composable
    override fun present(): CreateChildProfileState {
        val scope = rememberCoroutineScope()

        var fullName by remember { mutableStateOf("") }
        var displayName by remember { mutableStateOf("") }
        var dateOfBirth by remember { mutableStateOf("") }
        var isSaving by remember { mutableStateOf(false) }
        var errorMessage by remember { mutableStateOf<String?>(null) }

        fun onEvent(event: CreateChildProfileEvent) {
            when (event) {
                CreateChildProfileEvent.Back -> navigator.pop(
                    CreateChildProfileResult(CreateChildProfileResult.Status.Cancelled)
                )
                CreateChildProfileEvent.Continue -> {
                    if (isSaving) return
                    isSaving = true

                    scope.launch {
                        try {
                            val user = getCurrentUserUseCase() ?: throw IllegalStateException("No user")
                            val userId = user.id
                            val now = System.now()

                            // Parse date of birth (mm/dd/yyyy format)
                            val parsedDateOfBirth = dateOfBirth.takeIf { it.isNotBlank() }?.let {
                                try {
                                    val parts = it.split("/")
                                    if (parts.size == 3) {
                                        LocalDate(parts[2].toInt(), parts[0].toInt(), parts[1].toInt())
                                    } else null
                                } catch (e: Exception) {
                                    null
                                }
                            } ?: LocalDate(2020, 1, 1) // Default date

                            // Create child
                            val child = Child(
                                id = "",
                                fullName = fullName,
                                displayName = displayName.takeIf { it.isNotBlank() },
                                dateOfBirth = parsedDateOfBirth,
                                primaryLanguages = emptyList(),
                                stewardUserIds = listOf(userId),
                                status = ChildStatus.Active,
                                createdAt = now,
                                createdBy = userId,
                                modifiedAt = now,
                                modifiedBy = userId,
                            )

                            val savedChild = saveChildUseCase(child)

                            // Pop with result and navigate to child detail screen
                            navigator.pop(CreateChildProfileResult(CreateChildProfileResult.Status.Created))
                            navigator.goTo(ChildDetailScreen(childId = savedChild.id))
                        } catch (e: Exception) {
                            errorMessage = "We couldn't save the child profile. Please try again."
                        } finally {
                            isSaving = false
                        }
                    }
                }
                CreateChildProfileEvent.ScanQRCode -> {
                    // TODO: Implement QR code scanning
                }
                is CreateChildProfileEvent.UpdateFullName -> fullName = event.value
                is CreateChildProfileEvent.UpdateDisplayName -> displayName = event.value
                is CreateChildProfileEvent.UpdateDateOfBirth -> dateOfBirth = event.value
            }
        }

        return CreateChildProfileState(
            fullName = fullName,
            displayName = displayName,
            dateOfBirth = dateOfBirth,
            isSaving = isSaving,
            errorMessage = errorMessage,
            onBoardingMode = screen.showQrCodeScanButton,
            eventSink = ::onEvent,
        )
    }
}
